import { useEffect, useState } from "react"

import {
  useLazyGetIntelligenceReportingPeriodQuery,
  useLazyGetIntelligenceReportsQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { parseDateField } from "utils/dateUtils"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { ButtonNew } from "components/form/ButtonNew"

import { IntelligenceReportDrawer, type IntelligenceReportDrawerRow } from "./components/IntelligenceReportDrawer"
import { IntelligenceReportsEmptyState } from "./components/IntelligenceReportsEmptyState"
import { IntelligenceReportsHeader } from "./components/IntelligenceReportsHeader"
import { IntelligenceReportsTable } from "./components/IntelligenceReportsTable"
import { convertIntelligenceReportsToTableRows } from "./convertIntelligenceReportsToTableRows"
import IntelligenceReportsInfoBanner from "./IntelligenceReportsInfoBanner"
import intelligenceReportsTableHeaderCells from "./intelligenceReportsTableHeaderCells"

const IntelligenceReportsPage = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasIntelligenceReportsAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.IntelligenceReports)
    : false
  const [drawerReport, setDrawerReport] = useState<IntelligenceReportDrawerRow | null>(null)
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const activePriorityTab = (urlQueryParams.priority as string) || "high"

  const [fetchReportingPeriod, { data: periodData }] = useLazyGetIntelligenceReportingPeriodQuery()
  const [fetchReports, { data: reportsData, isFetching, isUninitialized }] = useLazyGetIntelligenceReportsQuery()

  useEffect(() => {
    if (!hasIntelligenceReportsAccess) return
    fetchReportingPeriod()
  }, [hasIntelligenceReportsAccess])

  useEffect(() => {
    if (!hasIntelligenceReportsAccess) return
    if (!periodData) return

    fetchReports({
      ...urlQueryParams,
      start_ts_utc: periodData.data.start_ts_utc,
      end_ts_utc: periodData.data.end_ts_utc,
      priority: activePriorityTab,
    } as UITypes.Params)
  }, [periodData, urlQueryParams, activePriorityTab, hasIntelligenceReportsAccess])

  const reportingPeriodLabel = periodData
    ? `${parseDateField(periodData.data.start_ts_utc)} - ${parseDateField(periodData.data.end_ts_utc)}`
    : null

  const tableData = reportsData ? convertIntelligenceReportsToTableRows(reportsData.data) : []
  const meta = reportsData?.meta

  const handlePriorityTabClick = (priority: string) => {
    if (priority === "") {
      setUrlQueryParams({ priority: null, page: 1 })
    } else {
      setUrlQueryParams({ priority, page: 1 })
    }
  }

  const hasSearchFilters = !!(urlQueryParams.sn || urlQueryParams.customer_name || urlQueryParams.customer_email)
  const showEmptyState = !isUninitialized && !isFetching && tableData.length === 0 && !hasSearchFilters

  const { handleLocalExport } = useExportCSV({
    params: {},
    headerCells: intelligenceReportsTableHeaderCells,
    fileName: "intelligence_reports",
  })

  return (
    <>
      <IntelligenceReportDrawer report={drawerReport} onClose={() => setDrawerReport(null)} />
      {!hasIntelligenceReportsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.IntelligenceReports} />}
      <IntelligenceReportsInfoBanner />
      <ContentWrapper
        title="Reports"
        renderHeaderAccessoryView={() => (
          <ButtonNew onClick={() => handleLocalExport(tableData)} disabled={!meta || meta.total === 0}>
            Export
          </ButtonNew>
        )}
      >
        <IntelligenceReportsHeader
          reportingPeriodLabel={reportingPeriodLabel}
          activePriorityTab={activePriorityTab}
          onPriorityTabClick={handlePriorityTabClick}
        />
        {showEmptyState ? (
          <IntelligenceReportsEmptyState priority={activePriorityTab} />
        ) : (
          <IntelligenceReportsTable
            data={tableData}
            isLoading={isFetching}
            meta={meta}
            onViewReport={setDrawerReport}
            headerCells={intelligenceReportsTableHeaderCells}
          />
        )}
      </ContentWrapper>
    </>
  )
}

export default IntelligenceReportsPage
